import { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import Modal from '../ui/Modal'
import { BookerWithTarget } from '../../types'
import { useBulkSetTargets } from '../../hooks/api/useTargets'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'

interface SetTargetsModalProps {
  open: boolean
  onClose: () => void
  bookers: BookerWithTarget[]
  month: number
  year: number
}

interface TargetValues {
  [bookerId: string]: {
    ctn: number
    bills: number
  }
}

const SetTargetsModal = ({ open, onClose, bookers, month, year }: SetTargetsModalProps) => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const [targets, setTargets] = useState<TargetValues>({})
  const bulkSetTargetsMutation = useBulkSetTargets()

  // Initialize targets from bookers data
  useEffect(() => {
    if (bookers.length > 0) {
      const initialTargets: TargetValues = {}
      bookers.forEach((booker) => {
        initialTargets[booker.id] = {
          ctn: booker.target_ctn || 0,
          bills: booker.target_bills || 0,
        }
      })
      setTargets(initialTargets)
    }
  }, [bookers])

  const handleTargetChange = (bookerId: string, field: 'ctn' | 'bills', value: string) => {
    const numValue = parseInt(value) || 0
    setTargets((prev) => ({
      ...prev,
      [bookerId]: {
        ...prev[bookerId],
        [field]: numValue,
      },
    }))
  }

  const handleSave = async () => {
    const targetsArray = Object.entries(targets).map(([bookerId, values]) => ({
      booker_id: bookerId,
      target_ctn: values.ctn,
      target_bills: values.bills,
    }))

    try {
      await bulkSetTargetsMutation.mutateAsync({
        targets: targetsArray,
        month,
        year,
      })
      onClose()
    } catch (error) {
      console.error('Failed to save targets:', error)
    }
  }

  const getTotals = () => {
    return Object.values(targets).reduce(
      (acc, val) => ({
        ctn: acc.ctn + val.ctn,
        bills: acc.bills + val.bills,
      }),
      { ctn: 0, bills: 0 }
    )
  }

  const totals = getTotals()
  const getMonthName = (m: number) => {
    const date = new Date(2000, m - 1, 1)
    return date.toLocaleString('default', { month: 'long' })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Set Monthly Targets - ${getMonthName(month)} ${year}`}
      onConfirm={handleSave}
      confirmText="Save Targets"
      loading={bulkSetTargetsMutation.isPending}
      size="medium"
    >
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Booker</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{uom.code} Target</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Bills Target</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookers.map((booker) => (
              <TableRow key={booker.id} hover>
                <TableCell>{booker.name}</TableCell>
                <TableCell align="right">
                  <TextField
                    type="number"
                    size="small"
                    value={targets[booker.id]?.ctn || 0}
                    onChange={(e) => handleTargetChange(booker.id, 'ctn', e.target.value)}
                    inputProps={{ min: 0, style: { textAlign: 'right' } }}
                    sx={{ width: 120 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    type="number"
                    size="small"
                    value={targets[booker.id]?.bills || 0}
                    onChange={(e) => handleTargetChange(booker.id, 'bills', e.target.value)}
                    inputProps={{ min: 0, style: { textAlign: 'right' } }}
                    sx={{ width: 120 }}
                  />
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totals.ctn.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {totals.bills.toLocaleString()}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Set monthly targets for each booker. Changes will be saved for {getMonthName(month)} {year}.
        </Typography>
      </Box>
    </Modal>
  )
}

export default SetTargetsModal
